import {
  transformInstitutionsList,
  transformProgramsList,
  transformProgramMembership,
  transformSearchUsers,
  transformUser,
  transformUsersList,
  transformUserInvitation,
  transformUserInvitationsList,
  transformSearchUserInvitations,
  transformAssessmentsList,
  transformAssessment,
  transformQuestionsList,
  transformNetworksList,
  transformSiblingUsersForCreate,
  transformUsersByProgram
} from "./response-transformers";

export interface OperationMapping {
  restEndpoint: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  transformer: (restResponse: any, variables?: any) => any;
  urlBuilder?: (variables: any) => string;
  handleNotFound?: (variables?: any) => any;
}

export const OPERATION_MAPPINGS: Record<string, OperationMapping> = {
  // Users (keys must match GraphQL operation names exactly)
  ListUsers: {
    restEndpoint: "/api/users",
    method: "GET",
    transformer: transformUsersList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      const filter: any = {};
      if (vars?.filter?.email?.contains)
        filter.email = { contains: vars.filter.email.contains };
      if (vars?.filter?.firstName?.contains)
        filter.firstName = { contains: vars.filter.firstName.contains };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/users?${params.toString()}`;
    }
  },

  // Get single ProgramMembership (used by UserReport individual report page)
  GetProgramMembership: {
    restEndpoint: "/api/program-membership/{id}",
    method: "GET",
    transformer: transformProgramMembership,
    urlBuilder: (vars: any) =>
      `/api/program-membership/${encodeURIComponent(vars?.id || "")}`,
    handleNotFound: () => ({
      data: { getProgramMembership: null }
    })
  },

  // UsersByProgram (used by fetchProgramMembershipByUserAndProgram for UserReport)
  UsersByProgram: {
    restEndpoint: "/api/program-memberships/search",
    method: "GET",
    transformer: transformUsersByProgram,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      const filter: any = {};
      if (vars?.programId) filter.programId = { eq: vars.programId };
      if (vars?.filter?.programMemberId?.eq)
        filter.programMemberId = { eq: vars.filter.programMemberId.eq };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/program-memberships/search${
        params.toString() ? `?${params.toString()}` : ""
      }`;
    }
  },

  // Search ProgramMemberships (used by Program Admin tables)
  SearchProgramMemberships: {
    restEndpoint: "/api/program-memberships/search",
    method: "GET",
    transformer: (restResponse: any) => ({
      data: {
        searchProgramMemberships: {
          items: (restResponse.programmemberships || []).map((pm: any) => ({
            ...pm,
            __typename: "ProgramMembership",
            program: pm.program
              ? {
                  ...pm.program,
                  __typename: "Program",
                  institution: pm.program.institution
                    ? { ...pm.program.institution, __typename: "Institution" }
                    : null
                }
              : null,
            programMember: pm.programMember
              ? {
                  ...pm.programMember,
                  __typename: "User",
                  institution: pm.programMember.institution
                    ? {
                        ...pm.programMember.institution,
                        __typename: "Institution"
                      }
                    : null
                }
              : null
          })),
          total:
            typeof restResponse.total === "number"
              ? restResponse.total
              : (restResponse.programmemberships || []).length,
          nextToken: restResponse.nextToken || null,
          __typename: "SearchableProgramMembershipConnection"
        }
      }
    }),
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));

      // Map to REST filter subset supported by search-program-memberships Lambda:
      // programId(s), programMemberId, programRole, traineeType, endDate.activeOnly.
      // We only forward OR clauses that are programId-based; other OR conditions are not
      // supported by the Lambda and are intentionally not forwarded.
      const f = vars?.filter || {};
      const filter: any = {};
      const programIds: string[] = [];
      const addPid = (pid: string | undefined) => {
        if (pid && !programIds.includes(pid)) programIds.push(pid);
      };

      // Top-level programId/programMemberId/role/type
      if (f?.programId?.eq) addPid(f.programId.eq);
      if (f?.programMemberId?.eq)
        filter.programMemberId = { eq: f.programMemberId.eq };
      if (f?.programRole?.eq) filter.programRole = { eq: f.programRole.eq };
      if (f?.traineeType?.eq) filter.traineeType = { eq: f.traineeType.eq };

      // or: [ { programId: { eq } }, ... ] — collect all program IDs (PA "All" sends multiple)
      if (Array.isArray(f?.or)) {
        for (const c of f.or) {
          const pid = (c as any)?.programId?.eq;
          if (pid) addPid(pid);
        }
      }

      // and: hoist nested equals and detect activeOnly from endDate OR
      if (Array.isArray(f?.and)) {
        for (const clause of f.and) {
          if (clause && typeof clause === "object") {
            if (clause.programId?.eq) addPid(clause.programId.eq);
            if (clause.programMemberId?.eq && !filter.programMemberId)
              filter.programMemberId = { eq: clause.programMemberId.eq };
            if (clause.programRole?.eq && !filter.programRole)
              filter.programRole = { eq: clause.programRole.eq };
            if (clause.traineeType?.eq && !filter.traineeType)
              filter.traineeType = { eq: clause.traineeType.eq };

            if (Array.isArray(clause.or)) {
              const hasEndDateExistsFalse = clause.or.some(
                (x: any) => x?.endDate?.exists === false
              );
              const hasEndDateEqEmpty = clause.or.some(
                (x: any) =>
                  typeof x?.endDate?.eq === "string" && x.endDate.eq === ""
              );
              if (hasEndDateExistsFalse || hasEndDateEqEmpty) {
                filter.endDate = { activeOnly: true };
              }
              for (const c of clause.or) {
                const pid = (c as any)?.programId?.eq;
                if (pid) addPid(pid);
              }
            }
          }
        }
      }

      if (programIds.length === 1) filter.programId = { eq: programIds[0] };
      else if (programIds.length > 1)
        filter.or = programIds.map((pid) => ({ programId: { eq: pid } })); // only programId ORs; Lambda supports no other OR shape

      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));

      return `/api/program-memberships/search${
        params.toString() ? `?${params.toString()}` : ""
      }`;
    }
  },
  // GetSponsor (used for branding in header)
  GetSponsor: {
    restEndpoint: "/api/sponsor/{id}",
    method: "GET",
    transformer: (restResponse: any) => ({
      data: {
        getSponsor: {
          ...(restResponse || {}),
          __typename: "Sponsor",
          branding: restResponse?.branding
            ? {
                ...restResponse.branding,
                __typename: "SponsorBranding",
                colors: restResponse.branding.colors
                  ? {
                      ...restResponse.branding.colors,
                      __typename: "SponsorColors"
                    }
                  : null,
                logos: restResponse.branding.logos
                  ? {
                      ...restResponse.branding.logos,
                      __typename: "SponsorLogos"
                    }
                  : null
              }
            : null,
          networks: restResponse?.networks
            ? { ...restResponse.networks, __typename: "ModelNetworkConnection" }
            : {
                items: [],
                nextToken: null,
                __typename: "ModelNetworkConnection"
              }
        }
      }
    }),
    urlBuilder: (vars: any) =>
      `/api/sponsor/${encodeURIComponent(vars?.id || "")}`
  },
  UserStats: {
    restEndpoint: "/api/user/{userId}/stats",
    method: "GET",
    transformer: (restResponse: any) => ({
      data: {
        userStats: restResponse
          ? {
              ...restResponse,
              __typename: "UserStats"
            }
          : null
      }
    }),
    urlBuilder: (vars: any) =>
      `/api/user/${encodeURIComponent(vars?.userId || "")}/stats`,
    handleNotFound: () => ({
      data: { userStats: null }
    })
  },

  GetUser: {
    restEndpoint: "/api/user",
    method: "GET",
    transformer: transformUser,
    urlBuilder: (vars: any) => {
      // Support fetching by either id or email
      const id = vars?.id;
      const email =
        vars?.email || vars?.emailLower || vars?.lowerEmail || undefined;
      if (email) {
        return `/api/user?email=${encodeURIComponent(String(email))}`;
      }
      return `/api/user?userId=${encodeURIComponent(String(id || ""))}`;
    },
    handleNotFound: () => ({
      data: {
        getUser: null
      }
    })
  },

  // Users search (used for fetchUsersByEmail in useAuth)
  SearchUsers: {
    restEndpoint: "/api/users",
    method: "GET",
    transformer: transformSearchUsers,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      // Forward first sort entry if present
      const sort0 = Array.isArray(vars?.sort) ? vars.sort[0] : undefined;
      const sf = sort0?.field;
      const sd = sort0?.direction;
      // Allowed sort fields (backend maps to SQL columns)
      const allowed = new Set([
        "firstName",
        "lastName",
        "email",
        "jobTitle",
        "npi",
        "pgy",
        // derived/server-mapped fields
        "program",
        "institutionName",
        "trainingProgram",
        "status",
        "role"
      ]);
      if (sf && allowed.has(String(sf))) {
        params.set("sortField", String(sf));
        if (sd && (sd === "asc" || sd === "desc")) {
          params.set("sortDirection", sd);
        }
      }
      // Forward the incoming GraphQL-style filter as-is to REST
      if (vars?.filter) params.set("filter", JSON.stringify(vars.filter));
      const qs = params.toString();
      return `/api/users${qs ? `?${qs}` : ""}`;
    }
  },

  // Programs
  ListPrograms: {
    restEndpoint: "/api/programs",
    method: "GET",
    transformer: transformProgramsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      if (vars?.nextToken) params.set("nextToken", String(vars.nextToken));
      const f = vars?.filter || {};
      const filter: any = {};
      if (f?.name?.eq) filter.name = { eq: f.name.eq };
      if (f?.name?.contains) filter.name = { contains: f.name.contains };
      if (f?.name?.beginsWith) filter.name = { beginsWith: f.name.beginsWith };
      if (f?.institutionId?.eq)
        filter.institutionId = { eq: f.institutionId.eq };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      const qs = params.toString();
      return `/api/programs${qs ? `?${qs}` : ""}`;
    }
  },

  // Networks
  ListNetworks: {
    restEndpoint: "/api/networks",
    method: "GET",
    transformer: transformNetworksList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      const f = vars?.filter || {};
      const filter: any = {};
      if (f?.name?.eq) filter.name = { eq: f.name.eq };
      if (f?.name?.contains) filter.name = { contains: f.name.contains };
      if (f?.name?.beginsWith) filter.name = { beginsWith: f.name.beginsWith };
      if (f?.sponsorId?.eq) filter.sponsorId = { eq: f.sponsorId.eq };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      const qs = params.toString();
      return `/api/networks${qs ? `?${qs}` : ""}`;
    }
  },

  // Assessments
  ListAssessments: {
    restEndpoint: "/api/assessments",
    method: "GET",
    transformer: transformAssessmentsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      if (vars?.nextToken) params.set("nextToken", String(vars.nextToken));
      const filter: any = {};
      const f = vars?.filter || {};
      if (f?.id?.eq) filter.id = { eq: f.id.eq };
      if (f?.title?.contains) filter.title = { contains: f.title.contains };
      if (f?.title?.eq) filter.title = { eq: f.title.eq };
      if (f?.title?.beginsWith)
        filter.title = { beginsWith: f.title.beginsWith };
      if (f?.description?.contains)
        filter.description = { contains: f.description.contains };
      if (f?.instrumentId?.eq) filter.instrumentId = { eq: f.instrumentId.eq };
      if (f?.sponsorId?.eq) filter.sponsorId = { eq: f.sponsorId.eq };
      if (f?.authorId?.eq) filter.authorId = { eq: f.authorId.eq };
      if (typeof f?.published?.eq === "boolean")
        filter.published = { eq: !!f.published.eq };
      if (typeof f?.archived?.eq === "boolean")
        filter.archived = { eq: !!f.archived.eq };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/assessments?${params.toString()}`;
    }
  },

  GetAssessment: {
    restEndpoint: "/api/assessment/{id}",
    method: "GET",
    transformer: transformAssessment,
    urlBuilder: (vars: any) =>
      `/api/assessment/${encodeURIComponent(vars?.id || "")}`
  },

  EngagementReport: {
    restEndpoint: "/api/reports/engagement",
    method: "POST",
    transformer: (restResponse: any) => ({
      data: {
        engagementReport: {
          ...restResponse,
          __typename: "EngagementReportResult"
        }
      }
    })
  },

  assessmentHistoryForCCC: {
    restEndpoint: "/api/reports/assessment-history-ccc",
    method: "POST",
    transformer: (restResponse: any) => ({
      data: {
        paginatableAssessmentHistory: {
          ...restResponse,
          __typename: "PaginatableAssessmentHistoryResult"
        }
      }
    })
  },

  MilestoneReport: {
    restEndpoint: "/api/reports/milestone",
    method: "POST",
    transformer: (restResponse: any) => ({
      data: {
        milestoneReport: restResponse?.data ?? restResponse
      }
    })
  },

  RequestBulkCCCReport: {
    restEndpoint: "/api/reports/bulk-ccc",
    method: "POST",
    transformer: (restResponse: any) => ({
      data: {
        requestBulkCCCReport: restResponse?.data ?? restResponse
      }
    })
  },

  // Questions by instrument – used by fetchAssessment to enrich questions
  ListQuestions: {
    restEndpoint: "/api/questions-by-instrument",
    method: "GET",
    transformer: transformQuestionsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      const instId = vars?.filter?.instrumentId?.eq;
      if (instId) params.set("instrumentId", String(instId));
      if (vars?.limit) params.set("limit", String(vars.limit));
      return `/api/questions-by-instrument?${params.toString()}`;
    }
  },

  // Institutions
  ListInstitutions: {
    restEndpoint: "/api/institutions",
    method: "GET",
    transformer: transformInstitutionsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      if (vars?.nextToken) params.set("nextToken", String(vars.nextToken));
      const f = vars?.filter || {};
      const filter: any = {};
      if (f?.name?.eq) filter.name = { eq: f.name.eq };
      if (f?.name?.contains) filter.name = { contains: f.name.contains };
      if (f?.name?.beginsWith) filter.name = { beginsWith: f.name.beginsWith };
      if (f?.institutionId?.eq)
        filter.institutionId = { eq: f.institutionId.eq };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      const qs = params.toString();
      return `/api/institutions${qs ? `?${qs}` : ""}`;
    }
  },

  // Sibling users for Create flow (maps to available-participants)
  siblingUsersForCreate: {
    restEndpoint: "/api/available-participants",
    method: "GET",
    transformer: transformSiblingUsersForCreate,
    urlBuilder: (vars: any) => {
      // GraphQL variables: { input: { userId, query?, sortOrder? } }
      const input = vars?.input || {};
      const params = new URLSearchParams();
      if (input.userId) params.set("userId", String(input.userId));
      if (input.query) params.set("query", String(input.query));
      if (
        input.sortOrder &&
        (input.sortOrder === "asc" || input.sortOrder === "desc")
      ) {
        params.set("sortOrder", input.sortOrder);
      }
      return `/api/available-participants?${params.toString()}`;
    }
  },

  // User Invitations (keys must match GraphQL operation names exactly)
  ListUserInvitations: {
    restEndpoint: "/api/user-invitations",
    method: "GET",
    transformer: transformUserInvitationsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      const filter: any = {};
      // Support common filters used in list + custom queries
      if (vars?.filter?.email?.eq) filter.email = { eq: vars.filter.email.eq };
      if (vars?.filter?.email?.contains)
        filter.email = { contains: vars.filter.email.contains };
      if (vars?.filter?.emailLower?.eq)
        filter.emailLower = { eq: vars.filter.emailLower.eq };
      if (vars?.filter?.programId?.eq)
        filter.programId = { eq: vars.filter.programId.eq };
      if (vars?.filter?.institutionId?.eq)
        filter.institutionId = { eq: vars.filter.institutionId.eq };
      if (vars?.filter?.invitationStatus?.eq)
        filter.invitationStatus = { eq: vars.filter.invitationStatus.eq };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/user-invitations?${params.toString()}`;
    }
  },

  SearchUserInvitations: {
    restEndpoint: "/api/user-invitations/search",
    method: "GET",
    transformer: transformSearchUserInvitations,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      if (vars?.nextToken) params.set("nextToken", String(vars.nextToken));
      // Forward sort (supports email for now)
      const sort0 = Array.isArray(vars?.sort) ? vars.sort[0] : undefined;
      const sf = sort0?.field;
      const sd = sort0?.direction;
      if (sf && String(sf) === "email") {
        params.set("sortField", "email");
        if (sd && (sd === "asc" || sd === "desc")) {
          params.set("sortDirection", sd);
        }
      }
      // Map a subset of OpenSearch-like filters used by UI
      const filter: any = {};
      const f = vars?.filter || {};
      if (
        f?.email?.wildcard ||
        f?.email?.contains ||
        f?.email?.matchPhrase ||
        f?.email?.eq
      ) {
        let v =
          f.email.wildcard ||
          f.email.contains ||
          f.email.matchPhrase ||
          f.email.eq;
        if (typeof v === "string") v = v.replace(/\*/g, "");
        filter.email = { contains: String(v) };
      }
      // Invitation Status: prefer contains; fallback to eq
      if (f?.invitationStatus?.contains || f?.invitationStatus?.wildcard) {
        const v = String(
          f.invitationStatus.contains || f.invitationStatus.wildcard
        ).replace(/\*/g, "");
        filter.invitationStatus = { contains: v };
      } else if (f?.invitationStatus?.eq) {
        filter.invitationStatus = { eq: f.invitationStatus.eq };
      }
      // Program/Institution/Role: contains by name (text input on the grid)
      if (f?.program?.contains || f?.program?.wildcard) {
        const v = String(f.program.contains || f.program.wildcard).replace(
          /\*/g,
          ""
        );
        filter.program = { contains: v };
      }
      if (f?.institution?.contains || f?.institution?.wildcard) {
        const v = String(
          f.institution.contains || f.institution.wildcard
        ).replace(/\*/g, "");
        filter.institution = { contains: v };
      }
      if (f?.role?.contains || f?.role?.wildcard) {
        const v = String(f.role.contains || f.role.wildcard).replace(/\*/g, "");
        filter.role = { contains: v };
      }
      // Backwards compatibility: support id equals if present
      if (f?.programId?.eq) filter.programId = { eq: f.programId.eq };
      if (f?.institutionId?.eq)
        filter.institutionId = { eq: f.institutionId.eq };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/user-invitations/search?${params.toString()}`;
    }
  },

  GetUserInvitation: {
    restEndpoint: "/api/user-invitations/{id}",
    method: "GET",
    transformer: transformUserInvitation,
    urlBuilder: (vars: any) =>
      `/api/user-invitations/${encodeURIComponent(vars?.id || "")}`
  },

  // Custom query strings used by Program Admins table
  ListUserInvitationsByProgramIds: {
    restEndpoint: "/api/user-invitations",
    method: "GET",
    transformer: transformUserInvitationsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      if (vars?.limit) params.set("limit", String(vars.limit));
      const filter: any = {};
      const programIds = Array.isArray(vars?.programIds) ? vars.programIds : null;
      if (programIds?.length === 1) {
        filter.programId = { eq: programIds[0] };
      } else if (programIds?.length > 1) {
        filter.or = programIds.map((pid: string) => ({ programId: { eq: pid } }));
      } else if (vars?.programId) {
        filter.programId = { eq: vars.programId };
      }
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/user-invitations?${params.toString()}`;
    }
  },

  // Email-based variants used in lookups
  UserInvitationsByEmailLower: {
    restEndpoint: "/api/user-invitations",
    method: "GET",
    transformer: transformUserInvitationsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      const filter: any = {};
      if (vars?.emailLower) filter.emailLower = { eq: String(vars.emailLower) };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/user-invitations?${params.toString()}`;
    }
  },
  // Unauthed variant used on some public flows; route to same REST with filter
  GetUserInvitationsByEmailUnauthed: {
    restEndpoint: "/api/user-invitations",
    method: "GET",
    transformer: transformUserInvitationsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      const filter: any = {};
      if (vars?.emailLower) filter.emailLower = { eq: String(vars.emailLower) };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/user-invitations?${params.toString()}`;
    }
  },
  UserInvitationsByEmail: {
    restEndpoint: "/api/user-invitations",
    method: "GET",
    transformer: transformUserInvitationsList,
    urlBuilder: (vars: any) => {
      const params = new URLSearchParams();
      const filter: any = {};
      if (vars?.email) filter.email = { eq: String(vars.email) };
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      return `/api/user-invitations?${params.toString()}`;
    }
  },

  // Network-program linkage listing (maps GraphQL ListNetworkPrograms)
  ListNetworkPrograms: {
    restEndpoint: "/api/network-programs",
    method: "GET",
    // Reuse networks list transformer pattern by returning GraphQL connection shape inline
    transformer: (restResponse: any) => ({
      data: {
        listNetworkPrograms: {
          items: (restResponse.networkPrograms || []).map((np: any) => ({
            ...np,
            __typename: "NetworkProgram",
            program: np.program
              ? { ...np.program, __typename: "Program" }
              : null,
            network: np.network
              ? {
                  ...np.network,
                  __typename: "Network",
                  sponsor: np.network.sponsor
                    ? { ...np.network.sponsor, __typename: "Sponsor" }
                    : null
                }
              : null
          })),
          nextToken: null,
          __typename: "ModelNetworkProgramConnection"
        }
      }
    }),
    urlBuilder: (vars: any) => {
      // Support filters like { or: [{ programID: { eq }}, { networkID: { eq }}] }
      const params = new URLSearchParams();
      const f = vars?.filter || {};
      // Flatten common patterns for programID/networkID equals and OR arrays
      const filter: any = {};
      if (f?.programID?.eq) filter.programID = { eq: f.programID.eq };
      if (f?.networkID?.eq) filter.networkID = { eq: f.networkID.eq };
      if (Array.isArray(f?.or)) {
        filter.or = f.or
          .map((clause: any) => {
            if (clause?.programID?.eq)
              return { programID: { eq: clause.programID.eq } };
            if (clause?.networkID?.eq)
              return { networkID: { eq: clause.networkID.eq } };
            return null;
          })
          .filter(Boolean);
      }
      if (Object.keys(filter).length > 0)
        params.set("filter", JSON.stringify(filter));
      if (vars?.limit) params.set("limit", String(vars.limit));
      return `/api/network-programs${
        params.toString() ? `?${params.toString()}` : ""
      }`;
    }
  }
};
